import React, { FC, useEffect, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Box, CircularProgress, Grid, Tooltip, useTheme } from '@mui/material';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import CustomTable from 'src/ui-component/CustomTable/Index';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useParams } from 'react-router-dom';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { useDispatch } from 'react-redux';
import getEnvVars from 'src/config/env';
import CustomButtonContained from '../CustomButton/CustomButtonContained';
import CustomButtonOutlined from '../CustomButton/CustomButtonOutlined';
import CustomModal from 'src/ui-component/customModal/CustomModal';
import { shouldShowEdit } from 'src/utils/CommonFunctions';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import {
  MAX_UPLOAD_FILE_SIZE_BYTES,
  MAX_UPLOAD_FILE_SIZE_ERROR,
  MAX_UPLOAD_FILE_SIZE_LABEL
} from 'src/constants/uploadLimits';

interface Props {
  applicationDetails: {
    applicationID: string;
    TownshipID: string;
    UserID: string;
    ApplicationStatus: string;
    PermitName?: string;
  };
}

interface FileDataType {
  downloadURL: string;
  fileName: string;
}

const UploadAndViewDocs: FC<Props> = ({ applicationDetails }) => {
  const dispatch = useDispatch();
  const { TownshipID, applicationID } = applicationDetails;
  const { permit } = useParams();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [openDeletePopup, setOpenDeletePopup] = useState<boolean>(false);
  const [deleteDoc, setDeleteDoc] = useState<any>({});
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [formateError, setFormateError] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [dataOnFileChange, setDataOnFileChange] = useState<FileDataType>();
  const [uploadedDocList, setUploadedDocList] = useState<any>({
    columns: ['File Name', 'Upload Date', 'Actions'],
    rows: []
  });
  const theme = useTheme();

  const getFullUrl = (url: string): string => {
    if (url && url.startsWith('http')) {
      return url;
    }
    const { socketUrl } = getEnvVars();
    const backendUrl = socketUrl || 'http://localhost:5000';
    return `${backendUrl}${url}`;
  };

  const handlePreviewFile = async (url: string) => {
    const blobName = url.split('/').slice(4).join('/');
    if (!blobName) {
      toast.error('Invalid file URL');
      return;
    }

    const newWindow = window.open('about:blank', '_blank');

    try {
      const response = await httpRequest(
        'GET',
        `/uploadPDF/view?blobName=${blobName}`
      );
      if (response?.fileURL) {
        if (newWindow) {
          newWindow.location.href = response.fileURL;
        }
      } else {
        newWindow?.close();
        toast.error('No file URL returned');
      }
    } catch (err) {
      newWindow?.close();
      toast.error(
        'Error fetching file preview: ' +
        (err instanceof Error ? err.message : 'Unknown error')
      );
    }
  };

  const actions = (item) => {
    const fullUrl = getFullUrl(item?.downloadURL || '');
    const isPetPermit = permit === '10';

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Tooltip title="View" arrow>
          <IconButton
            color="inherit"
            size="small"
            href={!isPetPermit ? fullUrl : undefined}
            target={!isPetPermit ? '_blank' : undefined}
            onClick={isPetPermit ? () => handlePreviewFile(item?.DocumentURL) : undefined}
          >
            <VisibilityIcon sx={{ color: 'black' }} fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete" arrow>
          <IconButton
            sx={{
              '&:hover': { background: theme.colors.error.lighter },
              color: theme.palette.error.main
            }}
            color="inherit"
            size="small"
            disabled={deleteLoading || isUploading}
            onClick={() => {
              setOpenDeletePopup(true);
              setDeleteDoc(item);
            }}
          >
            {deleteLoading ? (
              <CircularProgress size={24} />
            ) : (
              <DeleteForeverIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const getUploadedDocuments = async () => {
    const response = await httpRequest(
      'GET',
      `/uploadPDF/?ApplicationID=${applicationID}&PermitID=${permit}`
    );
    const data = await response?.data;
    if (response?.status === 'success') {
      const abstractedData =
        data &&
        data?.length &&
        data?.map((item) => {
          return {
            documentName: item?.DocumentName,
            insertDate: format(new Date(item?.InsertDate), 'MM/dd/yyyy'),
            Action: actions(item)
          };
        });
      setUploadedDocList({ ...uploadedDocList, rows: abstractedData });
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  useEffect(() => {
    if (TownshipID && applicationID) {
      getUploadedDocuments();
    }
  }, [applicationID]);

  const handleDeleteDocument = async () => {
    const documentData = deleteDoc;
    setDeleteLoading(true);
    const reqObj = {
      ApplicationID: documentData?.ApplicationID,
      PermitID: permit
    };
    const response = await httpRequest(
      'DELETE',
      `/uploadPDF?ApplicationDocsID=${documentData?.ApplicationDocsID}`,
      reqObj
    );
    if (response?.status === 'success') {
      setOpenDeletePopup(false);
      toast.success('Document deleted successfully.');
      getUploadedDocuments();
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
    setDeleteLoading(false);
  };

  const resetFileSelection = () => {
    setFile(null);
    setDataOnFileChange(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files?.length || isUploading) {
      return;
    }

    const selectedFiles = Array.from(event.target.files || []);
    const allowedImageTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/svg+xml',
      'image/heic'
    ];
    const allowedPdfType = 'application/pdf';

    const validFiles = selectedFiles.filter((fileItem) => {
      const isValidImage =
        allowedImageTypes.includes(fileItem.type) ||
        fileItem.name.toLowerCase().endsWith('.heic');
      const isValidPdf = fileItem.type === allowedPdfType;
      const isValidSize = fileItem.size <= MAX_UPLOAD_FILE_SIZE_BYTES;
      return isValidSize && (isValidImage || isValidPdf);
    });

    if (validFiles.length === 0 && selectedFiles.length > 0) {
      const sizeError = selectedFiles.some(
        (fileItem) => fileItem.size > MAX_UPLOAD_FILE_SIZE_BYTES
      );
      setFormateError(
        sizeError
          ? MAX_UPLOAD_FILE_SIZE_ERROR
          : 'Please upload image file (JPG, PNG, GIF, SVG, HEIC) or PDF file only.'
      );
      resetFileSelection();
      event.target.value = '';
      return;
    }

    if (validFiles.length === 0) {
      return;
    }

    setFormateError('');
    setIsUploading(true);

    const formData = new FormData();
    formData.append('uploadPDF', validFiles[0]);

    const url = `/uploadPDF/?permitName=${Number(permit) === 2
      ? 'Road'
      : Number(permit) === 4
        ? 'Tree'
        : Number(permit) === 6
          ? 'Solicitor'
          : Number(permit) === 7
            ? 'UsedCar'
            : Number(permit) === 1
              ? 'Panning and Zoning'
              : Number(permit) === 10
                ? 'Pet'
                : Number(permit) === 9
                  ? 'Construction'
                  : applicationDetails?.PermitName
      }`;

    try {
      const response = await httpRequest('POST', url, formData, {
        'Content-Type': 'multipart/form-data'
      });
      if (response?.status === 'success') {
        setDataOnFileChange(response?.data);
        setFile(validFiles[0]);
      } else {
        toast.error(response?.message);
        dispatch(handleError(response?.message));
        resetFileSelection();
      }
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleUploadDocument = async () => {
    if (!applicationDetails || isUploading) {
      return;
    }
    if (!dataOnFileChange?.fileName || !dataOnFileChange?.downloadURL) {
      toast.error('Please select a valid file before uploading.');
      return;
    }
    if (formateError) {
      return;
    }

    setIsUploading(true);
    try {
      const reqObj = {
        ApplicationID: applicationDetails?.applicationID,
        PermitID: permit,
        DocumentFileUpload: dataOnFileChange?.fileName,
        DocumentURL: dataOnFileChange?.downloadURL,
      };
      const response = await httpRequest('POST', '/uploadPDF/insert_PDF', reqObj);
      if (response?.status === 'success') {
        toast.success('Document uploaded successfully.');
        await getUploadedDocuments();
        resetFileSelection();
        setFormateError('');
      } else {
        toast.error(response?.message);
        dispatch(handleError(response?.message));
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <React.Fragment>
      {isUploading && <LoadingOverlay message="Uploading..." />}

      {!shouldShowEdit(applicationDetails) && (
        <>
          <Typography
            variant="body1"
            my={2}
            sx={{
              fontWeight: 'bold',
              fontSize: '20px',
              display: 'flex',
              flex: 1,
              color: '#ffffff',
              background: theme.palette.primary.main,
              lineHeight: 2
            }}
            justifyContent={'center'}
            alignItems={'center'}
          >
            Upload Documents
          </Typography>
          <Typography fontSize={13} mb={1}>
            Only image files (JPG, PNG, GIF, SVG, HEIC) and PDF files are allowed, with maximum file size of {MAX_UPLOAD_FILE_SIZE_LABEL}.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <input
              ref={fileInputRef}
              accept="application/pdf,image/jpeg,image/png,image/gif,image/svg+xml,image/heic"
              type="file"
              onChange={handleFileChange}
              disabled={isUploading}
              style={{ display: 'none' }}
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <CustomButtonOutlined
                component="span"
                color="primary"
                disabled={isUploading}
                sx={{
                  marginRight: '10px',
                  pointerEvents: isUploading ? 'none' : 'auto',
                  opacity: isUploading ? 0.6 : 1
                }}
                title={'Upload Document'}
                startIcon={<CloudUploadIcon />}
              />
            </label>
          </Box>
          {formateError && (
            <Typography mt={1} color="rgb(167, 42, 42)">{formateError} </Typography>
          )}

          {file?.name && (
            <Box
              sx={{
                display: { xs: 'block', sm: 'flex' },
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              mt={1}
            >
              <Typography display={'flex'} alignItems={'center'}>
                <FilePresentIcon color="disabled" fontSize="small" />
                {file?.name}
              </Typography>
              <Box sx={{ display: 'flex' }} mt={{ xs: 2, sm: 'auto' }}>
                <CustomButtonContained
                  sx={{ marginRight: '10px' }}
                  color="secondary"
                  disabled={isUploading}
                  title="Upload"
                  onClick={handleUploadDocument}
                />
                <CustomButtonOutlined
                  sx={{ color: '#8B0000', border: '1px solid #8B0000' }}
                  disabled={isUploading}
                  title={'Remove'}
                  onClick={() => {
                    resetFileSelection();
                    setFormateError('');
                  }}
                />
              </Box>
            </Box>
          )}
        </>
      )}

      <Grid container gap={2} mt={2} justifyContent={'space-between'}>
        <Grid
          item
          md={6}
          xs={12}
          sm={12}
          display={'flex'}
          alignItems={'center'}
        >
          <Typography variant="h3" fontSize={'22px'} mb={1.5} color={'#6E759F'}>
            List of Uploaded Documents
          </Typography>
        </Grid>
      </Grid>
      <Box mt={2}>
        {uploadedDocList?.rows?.length ? (
          <>
            <CustomTable
              pagination={0}
              setSelectedRowData={''}
              rows={uploadedDocList?.rows}
              columns={uploadedDocList.columns}
              isSearch={false}
            />
          </>
        ) : (
          <Typography fontSize={14} textAlign={'center'} mt={15}>
            No documents have been uploaded yet.
          </Typography>
        )}
      </Box>
      <CustomModal
        title={'Delete Document?'}
        IsOpen={openDeletePopup}
        setIsOpen={setOpenDeletePopup}
        onSave={handleDeleteDocument}
        actionButtons={['No', 'Yes']}
        loading={deleteLoading}
        content={
          <Typography>Are you sure you want to delete this document?</Typography>
        }
      />
    </React.Fragment>
  );
};

export default UploadAndViewDocs;
